import { useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faChevronLeft, faChevronRight, faCalendarDays } from '@fortawesome/free-solid-svg-icons';

const ENGAGEMENT_DEFINITIONS = {
  'Awaiting HQ Access': 'Invite not sent yet',
  'Not Activated': 'Invite sent, no HQ login',
  'Engaged': 'Actively logging into HQ',
  'Disengaged': 'No recent HQ login (past 14 days)',
};

const HOLIDAY_FIELDS = [
  { field: 'mlk_day', label: 'MLK Day' },
  { field: 'presidents_day', label: 'Presidents Day' },
  { field: 'memorial_day', label: 'Memorial Day' },
  { field: 'juneteenth', label: 'Juneteenth' },
  { field: 'labor_day', label: 'Labor Day' },
  { field: 'columbus_day', label: 'Columbus Day' },
  { field: 'veterans_day', label: 'Veterans Day' },
];

const TIMELINE_MILESTONES = [
  { phase: '🌐 Program Kickoff', label: 'Program Kickoff', dateField: 'program_start_date', color: '#3b82f6' },
  { phase: '🔐 Activation Check', label: 'Activation Check', dateField: 'activation_check', color: '#6366f1' },
  { phase: '📋 Census Roster', label: 'Census Roster', dateField: 'census_roster', color: '#a855f7' },
  { phase: '🚩 Early Support Check', label: 'Early Support Check', dateField: 'early_support_check', color: '#ec4899' },
  { phase: '📊 Mid Term Performance Analysis', label: 'Mid Term Performance Analysis', dateField: 'part_1_success_outlook', color: '#f97316' },
  { phase: '🎯 Completion Outlook', label: 'Completion Outlook', dateField: 'program_completion_outlook', color: '#14b8a6' },
  { phase: '🏁 Final Performance Summary', label: 'Final Performance Summary', dateField: 'final_performance_summary', color: '#22c55e' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function ymKey(year, month) {
  return year * 12 + month;
}

function parseYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m, day: d };
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function firstWeekday(year, month) {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

function formatTimelineDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function ProgramTimelineCard({ timeline }) {
  const today = timeline.today;
  const currentIdx = TIMELINE_MILESTONES.findIndex((m) => m.phase === timeline.current_phase);
  const lineEdge = `${100 / (TIMELINE_MILESTONES.length * 2)}%`;
  const [showCalendar, setShowCalendar] = useState(false);
  const canShowCalendar = !!timeline.program_start_date && !!timeline.program_end_date;
  return (
    <div className="bg-white px-4 py-4 rounded-3 shadow-sm mb-4">
      {(timeline.season || timeline.year) && (
        <div
          className="text-center text-muted text-uppercase fw-semibold"
          style={{ letterSpacing: '0.08em', fontSize: '0.8rem' }}
        >
          {[timeline.season, timeline.year].filter(Boolean).join(' ')}
        </div>
      )}
      <h2
        className="text-center fw-bold mb-4 mt-1"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '1.5rem' }}
      >
        Program Timeline
      </h2>
      <div className="d-flex justify-content-between align-items-start position-relative">
        <div
          className="position-absolute"
          style={{
            top: '0.6rem',
            left: lineEdge,
            right: lineEdge,
            height: '2px',
            background: '#e5e7eb',
            zIndex: 0,
          }}
        />
        {TIMELINE_MILESTONES.map((milestone, idx) => {
          const isCurrent = milestone.phase === timeline.current_phase;
          const dateValue = timeline[milestone.dateField];
          const isPast =
            !isCurrent &&
            ((currentIdx >= 0 && idx < currentIdx) ||
              (dateValue && today && dateValue < today));
          const dotColor = isCurrent ? '#0d6efd' : isPast ? '#198754' : '#cbd5e1';
          const dateStr = formatTimelineDate(dateValue);
          return (
            <div
              key={milestone.phase}
              className="text-center flex-fill position-relative px-3"
              style={{ zIndex: 1, minWidth: 0 }}
            >
              <div
                className="mx-auto rounded-circle"
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  background: dotColor,
                  border: isCurrent ? '3px solid #cfe2ff' : 'none',
                  boxShadow: isCurrent ? '0 0 0 2px #0d6efd' : 'none',
                }}
              />
              <div
                className="mt-3"
                style={{
                  fontSize: '0.75rem',
                  lineHeight: 1.35,
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent ? '#0d6efd' : '#374151',
                }}
              >
                {milestone.label}
              </div>
              <div
                className="mt-2 text-muted"
                style={{ fontSize: '0.72rem', lineHeight: 1.3 }}
              >
                {dateStr || '—'}
              </div>
            </div>
          );
        })}
      </div>
      {canShowCalendar && (
        <div className="text-center mt-4">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowCalendar(true)}
          >
            <FontAwesomeIcon icon={faCalendarDays} className="me-2" />
            Calendar View
          </Button>
        </div>
      )}
      {canShowCalendar && (
        <ProgramCalendarModal
          show={showCalendar}
          onHide={() => setShowCalendar(false)}
          timeline={timeline}
        />
      )}
    </div>
  );
}

function addDaysYMD(ymd, days) {
  const d = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  d.setUTCDate(d.getUTCDate() + days);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

function ymdString(ymd) {
  return `${ymd.year}-${pad2(ymd.month)}-${pad2(ymd.day)}`;
}

function ProgramCalendarModal({ show, onHide, timeline }) {
  const phaseSpans = useMemo(() => {
    const spans = [];
    for (let i = 0; i < TIMELINE_MILESTONES.length; i++) {
      const m = TIMELINE_MILESTONES[i];
      const start = timeline[m.dateField];
      if (!start) continue;
      let end = null;
      for (let j = i + 1; j < TIMELINE_MILESTONES.length; j++) {
        const nextStart = timeline[TIMELINE_MILESTONES[j].dateField];
        if (nextStart) {
          end = nextStart;
          break;
        }
      }
      if (!end) end = timeline.program_end_date;
      if (!end) continue;
      spans.push({ label: m.label, color: m.color, start, end });
    }
    return spans;
  }, [timeline]);

  const holidaysByDate = useMemo(() => {
    const map = {};
    for (const { field, label } of HOLIDAY_FIELDS) {
      const v = timeline[field];
      if (v) map[v] = label;
    }
    const tStart = parseYMD(timeline.thanksgiving_start);
    const tEnd = parseYMD(timeline.thanksgiving_end);
    if (tStart && tEnd) {
      let cur = tStart;
      const endStr = ymdString(tEnd);
      while (true) {
        map[ymdString(cur)] = 'Thanksgiving';
        if (ymdString(cur) === endStr) break;
        cur = addDaysYMD(cur, 1);
      }
    }
    return map;
  }, [timeline]);

  const startYMD = parseYMD(timeline.program_start_date);
  const endYMD = parseYMD(timeline.program_end_date);
  const minKey = startYMD ? ymKey(startYMD.year, startYMD.month) : null;
  const maxKey = endYMD ? ymKey(endYMD.year, endYMD.month) : null;

  const todayYMD = parseYMD(timeline.today);
  const initialKey = useMemo(() => {
    if (todayYMD && minKey !== null && maxKey !== null) {
      const k = ymKey(todayYMD.year, todayYMD.month);
      if (k < minKey) return minKey;
      if (k > maxKey) return maxKey;
      return k;
    }
    return minKey;
  }, [todayYMD, minKey, maxKey]);

  const [viewKey, setViewKey] = useState(initialKey);
  useEffect(() => {
    if (show) setViewKey(initialKey);
  }, [show, initialKey]);

  if (!startYMD || !endYMD || viewKey == null) return null;

  const viewYear = Math.floor(viewKey / 12);
  const viewMonth = viewKey - viewYear * 12;
  const canPrev = viewKey > minKey;
  const canNext = viewKey < maxKey;

  const numDays = daysInMonth(viewYear, viewMonth);
  const offset = firstWeekday(viewYear, viewMonth);
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= numDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const phaseForDay = (day) => {
    const dateStr = `${viewYear}-${pad2(viewMonth)}-${pad2(day)}`;
    for (const span of phaseSpans) {
      if (dateStr >= span.start && dateStr < span.end) return span;
    }
    return null;
  };

  const todayStr = timeline.today;
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>Program Calendar</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button
            variant="light"
            size="sm"
            onClick={() => setViewKey(viewKey - 1)}
            disabled={!canPrev}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          <div className="fw-semibold" style={{ fontSize: '1.05rem' }}>
            {MONTH_NAMES[viewMonth - 1]} {viewYear}
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={() => setViewKey(viewKey + 1)}
            disabled={!canNext}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
        </div>
        <div
          className="d-grid"
          style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}
        >
          {dayLabels.map((label) => (
            <div
              key={label}
              className="text-center text-muted fw-semibold"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0' }}
            >
              {label}
            </div>
          ))}
          {cells.map((day, idx) => {
            if (day == null) {
              return <div key={`empty-${idx}`} />;
            }
            const span = phaseForDay(day);
            const dateStr = `${viewYear}-${pad2(viewMonth)}-${pad2(day)}`;
            const isToday = todayStr === dateStr;
            const holiday = holidaysByDate[dateStr];
            const titleParts = [span?.label, holiday].filter(Boolean);
            return (
              <div
                key={`day-${day}`}
                className="rounded-2 d-flex flex-column align-items-center justify-content-start"
                style={{
                  aspectRatio: '1 / 1',
                  background: span ? `${span.color}22` : '#f8f9fa',
                  border: isToday ? '2px solid #0d6efd' : '1px solid #f1f3f5',
                  padding: '0.25rem',
                  fontSize: '0.8rem',
                  position: 'relative',
                }}
                title={titleParts.join(' · ')}
              >
                <div
                  className="fw-semibold"
                  style={{ color: isToday ? '#0d6efd' : '#374151' }}
                >
                  {day}
                </div>
                {holiday && (
                  <div
                    className="text-center text-muted lh-1 mt-1"
                    style={{
                      fontSize: '0.6rem',
                      fontStyle: 'italic',
                      wordBreak: 'break-word',
                    }}
                  >
                    {holiday}
                  </div>
                )}
                {span && (
                  <div
                    className="mt-auto w-100 rounded-1"
                    style={{
                      background: span.color,
                      height: '4px',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <div
            className="fw-semibold text-uppercase text-muted mb-2"
            style={{ letterSpacing: '0.05em', fontSize: '0.75rem' }}
          >
            Phases
          </div>
          <div className="d-flex flex-wrap gap-3">
            {phaseSpans.map((span) => (
              <div key={span.label} className="d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                <div
                  className="rounded-circle me-2"
                  style={{ width: '0.7rem', height: '0.7rem', background: span.color }}
                />
                {span.label}
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function EngagementInfo({ name }) {
  const meaning = ENGAGEMENT_DEFINITIONS[name];
  if (!meaning) return null;
  const popover = (
    <Popover id={`engagement-info-${name}`} className="border-0 shadow">
      <Popover.Body
        className="py-2 px-3 text-muted"
        style={{ fontSize: '0.82rem', lineHeight: 1.4 }}
      >
        {meaning}
      </Popover.Body>
    </Popover>
  );
  return (
    <span onClick={(ev) => ev.stopPropagation()}>
      <OverlayTrigger trigger="click" placement="top" overlay={popover} rootClose>
        <button
          type="button"
          className="btn btn-link p-0 ms-1 text-muted align-baseline"
          style={{ lineHeight: 1, fontSize: '0.8em', opacity: 0.55 }}
          aria-label={`What does ${name} mean?`}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
        </button>
      </OverlayTrigger>
    </span>
  );
}

function BreakdownRow({ label, count, percent, onClick, children }) {
  return (
    <tr
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={onClick ? 'breakdown-row' : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <td className="py-1">
        {onClick ? (
          <span className="breakdown-link">{label}</span>
        ) : (
          label
        )}
        {children}
      </td>
      <td className="py-1 text-end fw-semibold" style={{ width: '60px' }}>
        {count}
      </td>
      <td className="py-1 ps-3 text-muted" style={{ width: '70px' }}>
        ({percent}%)
      </td>
    </tr>
  );
}

export default function CourseOverview({ university, onNavigateToRoster }) {
  const [counts, setCounts] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!university?.university_id) return;
    setCounts(null);
    setEngagement(null);
    setTimeline(null);
    setError(null);
    const id = encodeURIComponent(university.university_id);
    const enrollmentsURL = `/api/active_enrollments/${id}`;
    const universityURL = `/api/university/${id}`;
    const fetchJSON = (url, accept) =>
      fetch(url, { headers: { Accept: accept } }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      });
    Promise.all([
      fetchJSON(enrollmentsURL, 'application/vnd.howdy.track-breakdown+json'),
      fetchJSON(enrollmentsURL, 'application/vnd.howdy.engagement-breakdown+json'),
      fetchJSON(universityURL, 'application/vnd.howdy.program-timeline+json'),
    ])
      .then(([track, eng, tl]) => {
        setCounts(track);
        setEngagement(eng);
        setTimeline(tl);
      })
      .catch((err) => setError(err.message));
  }, [university?.university_id]);

  const byTrack = counts ?? {};
  const tracks = Object.keys(byTrack).sort();
  const total = tracks.reduce((sum, name) => sum + byTrack[name], 0);

  const byEngagement = engagement ?? {};
  const engagementOrder = ['Awaiting HQ Access', 'Not Activated', 'Engaged', 'Disengaged'];
  const engagementStatuses = engagementOrder.filter((name) => byEngagement[name] > 0);
  const engagementTotal = engagementStatuses.reduce((sum, name) => sum + byEngagement[name], 0);

  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const engPct = (n) => (engagementTotal > 0 ? Math.round((n / engagementTotal) * 100) : 0);

  return (
    <Container className="py-5" style={{ maxWidth: '960px' }}>
      {timeline && <ProgramTimelineCard timeline={timeline} />}

      <div className="bg-white px-5 py-5 rounded-3 shadow-sm">
        <h1
          className="text-center fw-bold mb-5"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '2.25rem' }}
        >
          Student Breakdown
        </h1>

        {error && <Alert variant="danger">Failed to load student counts: {error}</Alert>}

        {!error && (!counts || !engagement) && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        )}

        {!error && counts && engagement && (
          <>
            <div className="text-center mb-5">
              <div
                className="fw-bold text-uppercase mb-2"
                style={{ letterSpacing: '0.05em', fontSize: '1rem' }}
              >
                Students
              </div>
              <hr className="mx-auto mb-3" style={{ maxWidth: '320px', opacity: 0.2 }} />
              <div className="fw-bold" style={{ fontSize: '2.5rem', lineHeight: 1 }}>
                {total}
              </div>
            </div>

            <div className="mt-5 row g-4">
              <div className="col-md-6">
                <div
                  className="fw-bold text-uppercase mb-2"
                  style={{ letterSpacing: '0.05em', fontSize: '0.95rem' }}
                >
                  Students by Track
                </div>
                <hr className="mb-3" style={{ opacity: 0.2 }} />
                {tracks.length === 0 ? (
                  <p className="text-muted mb-0">No active enrollments.</p>
                ) : (
                  <table className="w-100" style={{ fontSize: '1.05rem' }}>
                    <tbody>
                      {tracks.map((name) => (
                        <BreakdownRow
                          key={name}
                          label={name}
                          count={byTrack[name]}
                          percent={pct(byTrack[name])}
                          onClick={
                            onNavigateToRoster
                              ? () => onNavigateToRoster({ track: name })
                              : undefined
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="col-md-6">
                <div
                  className="fw-bold text-uppercase mb-2"
                  style={{ letterSpacing: '0.05em', fontSize: '0.95rem' }}
                >
                  Students by Engagement
                </div>
                <hr className="mb-3" style={{ opacity: 0.2 }} />
                {engagementStatuses.length === 0 ? (
                  <p className="text-muted mb-0">No engagement data.</p>
                ) : (
                  <table className="w-100" style={{ fontSize: '1.05rem' }}>
                    <tbody>
                      {engagementStatuses.map((name) => (
                        <BreakdownRow
                          key={name}
                          label={name}
                          count={byEngagement[name]}
                          percent={engPct(byEngagement[name])}
                          onClick={
                            onNavigateToRoster
                              ? () => onNavigateToRoster({ status: name })
                              : undefined
                          }
                        >
                          <EngagementInfo name={name} />
                        </BreakdownRow>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
